import { styled } from '@mui/material';
import { MaskedTextInput, Stack } from '@kc-console/common-ui';
import IcCalendarRegular from '@kep/kcds-icons/IcCalendarRegular';

import { datePickerClasses } from '../theme';
import { calcDatePickerInputMinWidth, getTimezoneOffset } from '../utils';

import type { ChangeEvent } from 'react';
import type { DatePickerInputInternalProps } from '../types';

interface DatePickerInputComponentProps extends DatePickerInputInternalProps {
  disabled?: boolean;
  error?: boolean;
  /**
   * input의 id 속성 (aria-labelledby로 연결하기 위해 사용)
   */
  id?: string;
  /**
   * 캘린더 팝업이 열렸는지 여부 (aria-expanded 속성에 사용)
   */
  isOpen?: boolean;
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string,
  ) => void;
  onFocus?: () => void;
  placeholder?: string;
  /**
   * DatePicker container element에 대한 ref
   * React 19에서는 forwardRef 없이 직접 props로 ref를 받을 수 있습니다.
   */
  ref?: React.Ref<HTMLDivElement>;
  value?: string;
}

const DatePickerInput = (props: DatePickerInputComponentProps) => {
  const {
    ref,
    mask,
    showTimezone = false,
    value,
    placeholder,
    error,
    disabled,
    onFocus,
    onChange,
    size = 'md',
    isOpen,
    id,
    ...restProps
  } = props;

  // range 선택 시 " - "를 " ~ "로 변경
  const modifiedValue = typeof value === 'string' ? value.replace(' - ', ' ~ ') : value;

  const hasInputDigits = typeof modifiedValue === 'string' && /\d/.test(modifiedValue);
  const displayValue = hasInputDigits ? modifiedValue : '';

  return (
    <Stack ref={ref} direction="horizontal" stretch spacing={1} alignItems={'center'}>
      <StyledInput
        {...restProps}
        id={id}
        size={size}
        value={displayValue}
        mask={mask}
        error={error}
        disabled={disabled}
        disabledClearable
        alwaysShowMask={true}
        onFocus={onFocus}
        onChange={onChange}
        endAdornment={<StyledIcCalendarRegular />}
        placeholder={placeholder}
        role="combobox"
        aria-invalid={error}
        aria-disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      />
      {showTimezone && <TimezoneText>{getTimezoneOffset()}</TimezoneText>}
    </Stack>
  );
};

DatePickerInput.displayName = 'DatePickerInput';

export default DatePickerInput;

const TimezoneText = styled('span', {
  target: datePickerClasses.timezone,
})`
  ${({ theme }) => theme.typography['body/normal/md']};
  white-space: nowrap;
`;

const StyledIcCalendarRegular = styled(IcCalendarRegular, {
  target: datePickerClasses.inputIcon,
})`
  color: ${({ theme }) => theme.color['text/default']};
`;

const StyledInput = styled(MaskedTextInput, {
  target: datePickerClasses.input,
})`
  min-width: ${({ mask }) => `${calcDatePickerInputMinWidth(mask)}px`};
`;
